import { useState } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { MuiTelInput } from "mui-tel-input";

import { __API_URL__ } from "../../SERVER_URL";
import useAddTrialInvestigatorMutation from "../../hooks/useAddTrialInvestigatorMutation";

const TrialCrc = ({
  type,
  setShowModal,
  toast,
  trialDetails,
  onReviewerChange,
  allReviewers,
  setAllReviewers,
  setAllInvestigators,
  allInvestigators,
  onInvestigatorChange,
  setReviewerChanged,
}) => {
  const token = localStorage.getItem("token");
  const [IsLoadingButton, setIsLoadingButton] = useState(false);
  const [addStaff, setAddStaff] = useState({
    first_name: "",
    last_name: "",
    email: "",
    max_patient: "",
    site_number: "",
    site_address: "",
    site_phone: "",
  });
  const addTrialInvestigatorMutation = useAddTrialInvestigatorMutation();

  const isManualSiteId =
    type === "investigator" &&
    trialDetails?.manual_investigator_id === true &&
    trialDetails?.trial_structure !== "session_based";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddStaff((addStaff) => ({
      ...addStaff,
      [name]: value,
    }));
  };

  const handleSitePhoneChange = (value) => {
    setAddStaff((addStaff) => ({
      ...addStaff,
      site_phone: value,
    }));
  };

  const addInvestigator = () => {
    addTrialInvestigatorMutation.mutate(
      {
        trialId: trialDetails.id,
        first_name: addStaff.first_name,
        last_name: addStaff.last_name,
        email: addStaff.email,
        max_patient: addStaff.max_patient,
        site_number: isManualSiteId ? addStaff.site_number : undefined,
        site_address: addStaff.site_address,
        site_phone: addStaff.site_phone,
      },
      {
        onSuccess: (reponseData) => {
          setShowModal(false);
          setAddStaff(false);

          toast.success("Investigator Added successfully");

          const filteredOptions = allInvestigators.filter(
            (option) => option.value !== reponseData.value
          );
          onInvestigatorChange(null, [...trialDetails.investigators, reponseData], "selectOption");
          setAllInvestigators(filteredOptions);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Something went wrong");
        },
        onSettled: () => {
          setIsLoadingButton(false);
        },
      }
    );
  };

  const addReviewer = () => {
    axios
      .post(
        `${__API_URL__}/api/practitioners/add_reviewer/`,
        { first_name: addStaff.first_name, last_name: addStaff.last_name, email: addStaff.email },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        const reponseData = res.data;
        setShowModal(false);
        setAddStaff(false);
        setIsLoadingButton(false);

        toast.success("Reviewer Added successfully");

        const filteredOptions = allReviewers.filter((option) => option.value !== reponseData.value);
        onReviewerChange(null, [...trialDetails.reviewers, reponseData], "selectOption");
        setAllReviewers(filteredOptions);
        setReviewerChanged(true);
      })
      .catch((err) => {
        setIsLoadingButton(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const Update = (e) => {
    e.preventDefault();

    for (let investigator of trialDetails.investigators) {
      if (investigator.value === addStaff.email) {
        toast.error("Investigator already exists.");
        return;
      }
    }

    setIsLoadingButton(true);

    if (type === "investigator") {
      addInvestigator();
    } else if (type === "reviewer") {
      addReviewer();
    }
  };

  return (
    <Dialog open onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add
        <IconButton
          onClick={() => setShowModal(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={(e) => Update(e)}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="First Name"
              size="small"
              name="first_name"
              value={addStaff?.first_name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              size="small"
              name="last_name"
              value={addStaff?.last_name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Email"
              size="small"
              type="email"
              name="email"
              value={addStaff?.email}
              onChange={handleChange}
              required
            />
            {type === "investigator" && (
              <TextField
                fullWidth
                label="Max Subjects"
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                name="max_patient"
                value={addStaff?.max_patient}
                onChange={handleChange}
                required
              />
            )}
            {isManualSiteId && (
              <TextField
                fullWidth
                label="Site Id"
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                name="site_number"
                value={addStaff?.site_number}
                onChange={handleChange}
                required
              />
            )}
            {type === "investigator" && (
              <TextField
                fullWidth
                label="Site Address"
                size="small"
                name="site_address"
                value={addStaff?.site_address}
                onChange={handleChange}
              />
            )}
            {type === "investigator" && (
              <MuiTelInput
                fullWidth
                label="Site Phone No."
                size="small"
                defaultCountry="US"
                disableFormatting
                value={addStaff?.site_phone}
                onChange={handleSitePhoneChange}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={IsLoadingButton}
            startIcon={IsLoadingButton ? <CircularProgress size={16} /> : null}
          >
            {IsLoadingButton ? "Loading" : "Save & Invite"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TrialCrc;
